import {
  DashboardDataTableColumnKind,
  DashboardTableStatusBadge,
  DashboardTableStatusTone,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { cn } from '@shigo-ui/ui';

import { formatNumber, formatPercent, formatRatio, formatUsd } from '../../../../../lib/format';

import {
  NetworkPerformanceHalf,
  NetworkPerformanceStatus,
  type NetworkPerformanceRow,
} from '../../model';

export enum NetworkPerformanceSortKey {
  Year = 'year',
  Month = 'month',
  Half = 'half',
  NetworkName = 'networkName',
  NetworkId = 'networkId',
  Revenue = 'revenue',
  Profit = 'profit',
  Requests = 'requests',
  Impressions = 'impressions',
  FillRate = 'fillRate',
  Ecpm = 'ecpm',
  BidRate = 'bidRate',
  BidFill = 'bidFill',
  WinRate = 'winRate',
  RevPerRequest = 'revPerRequest',
  FillTimesEcpm = 'fillTimesEcpm',
  StartMomentumPct = 'startMomentumPct',
  MomGrowthPct = 'momGrowthPct',
  TotalScore = 'totalScore',
  Status = 'status',
}

const networkPerformanceStatusTones: Record<NetworkPerformanceStatus, DashboardTableStatusTone> = {
  [NetworkPerformanceStatus.Scale]: DashboardTableStatusTone.Success,
  [NetworkPerformanceStatus.Optimize]: DashboardTableStatusTone.Warning,
  [NetworkPerformanceStatus.Fix]: DashboardTableStatusTone.Danger,
};

export const networkPerformanceColumns = buildDashboardDataTableColumns<NetworkPerformanceRow>([
  textColumn(NetworkPerformanceSortKey.Year, 'Year', (row) => row.year, 'w-24 pl-4'),
  textColumn(NetworkPerformanceSortKey.Month, 'Month', (row) => row.month, 'w-28'),
  {
    id: NetworkPerformanceSortKey.Half,
    kind: DashboardDataTableColumnKind.Custom,
    header: 'Half',
    sortable: true,
    sortKey: NetworkPerformanceSortKey.Half,
    headerClassName: 'w-24',
    className: 'w-24',
    cell: (row) => (
      <span
        className={cn(
          'inline-flex rounded-sm px-2 py-0.5 text-xs font-semibold',
          row.half === NetworkPerformanceHalf.Total
            ? 'bg-success/10 text-success'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {row.half}
      </span>
    ),
  },
  textColumn(NetworkPerformanceSortKey.NetworkName, 'Network', (row) => row.networkName, 'w-48'),
  textColumn(NetworkPerformanceSortKey.NetworkId, 'Network ID', (row) => row.networkId, 'w-28'),
  textColumn(NetworkPerformanceSortKey.Revenue, 'Revenue', (row) => formatUsd(row.revenue)),
  textColumn(NetworkPerformanceSortKey.Profit, 'Profit', (row) => formatUsd(row.profit)),
  textColumn(NetworkPerformanceSortKey.Requests, 'Requests', (row) =>
    formatNumber(row.requests, 2),
  ),
  textColumn(NetworkPerformanceSortKey.Impressions, 'Impressions', (row) =>
    formatNumber(row.impressions, 2),
  ),
  textColumn(NetworkPerformanceSortKey.FillRate, 'Fill rate', (row) => formatRatio(row.fillRate)),
  textColumn(NetworkPerformanceSortKey.Ecpm, 'eCPM', (row) => formatUsd(row.ecpm)),
  textColumn(NetworkPerformanceSortKey.BidRate, 'Bid rate', (row) => formatRatio(row.bidRate)),
  textColumn(NetworkPerformanceSortKey.BidFill, 'Bid fill', (row) => formatRatio(row.bidFill)),
  textColumn(NetworkPerformanceSortKey.WinRate, 'Win rate', (row) => formatRatio(row.winRate)),
  textColumn(
    NetworkPerformanceSortKey.RevPerRequest,
    'Rev/request',
    (row) => formatOptionalFixedCurrency(row.revPerRequest),
    'w-32',
  ),
  textColumn(
    NetworkPerformanceSortKey.FillTimesEcpm,
    'Fill x eCPM',
    (row) => formatNumber(row.fillTimesEcpm, 2),
    'w-32',
  ),
  textColumn(
    NetworkPerformanceSortKey.StartMomentumPct,
    'Start momentum',
    (row) => formatPercent(row.startMomentumPct),
    'w-36',
  ),
  textColumn(
    NetworkPerformanceSortKey.MomGrowthPct,
    'MoM growth',
    (row) => formatPercent(row.momGrowthPct),
    'w-32',
  ),
  textColumn(
    NetworkPerformanceSortKey.TotalScore,
    'Score',
    (row) => formatNumber(row.totalScore, 2),
    'w-24',
  ),
  {
    id: NetworkPerformanceSortKey.Status,
    kind: DashboardDataTableColumnKind.Custom,
    header: 'Status',
    sortable: true,
    sortKey: NetworkPerformanceSortKey.Status,
    headerClassName: 'w-28 pr-4',
    className: 'w-28 pr-4',
    cell: (row) => renderNetworkPerformanceStatusBadge(row.status),
  },
]);

function textColumn(
  id: NetworkPerformanceSortKey,
  header: string,
  value: (row: NetworkPerformanceRow) => string | number,
  width = 'w-28',
) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Text as const,
    header,
    sortable: true,
    sortKey: id,
    headerClassName: width,
    className: width,
    value,
  };
}

function formatOptionalFixedCurrency(value: number | null) {
  return value === null ? '-' : `$${value.toFixed(4)}`;
}

function renderNetworkPerformanceStatusBadge(status: NetworkPerformanceStatus | null) {
  if (!status) return '-';
  return (
    <DashboardTableStatusBadge tone={networkPerformanceStatusTones[status]}>
      {status}
    </DashboardTableStatusBadge>
  );
}
